import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, XCircle, RotateCcw, GraduationCap, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

export default function Quiz() {
  const { t, lang } = useLang();
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState("alla");
  const [count, setCount] = useState("10");
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/regions").then((r) => setRegions(r.data)).catch(() => {});
  }, [lang]);

  const start = async () => {
    setLoading(true);
    try {
      const params = { count: parseInt(count) };
      if (region !== "alla") params.region = region;
      const r = await api.get("/quiz", { params });
      setQuestions(r.data);
      setIdx(0); setAnswers({}); setSelected(null); setRevealed(false); setDone(false);
    } catch (e) {
      toast.error(t("quiz.startFail"));
    } finally {
      setLoading(false);
    }
  };

  const reveal = () => {
    if (selected === null) return toast.warning(t("quiz.selectFirst"));
    setRevealed(true);
    setAnswers({ ...answers, [idx]: selected });
  };

  const next = () => {
    if (idx + 1 >= questions.length) {
      setDone(true);
    } else {
      setIdx(idx + 1); setSelected(null); setRevealed(false);
    }
  };

  const score = Object.entries(answers).filter(([i, a]) => questions[i]?.correct_index === a).length;

  if (questions.length === 0) {
    return (
      <div data-testid="quiz-setup" className="max-w-xl">
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
          <span className="title-rule" />{t("quiz.eyebrow")}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight mb-3">{t("quiz.title")}</h1>
        <p className="text-muted-foreground mb-8">{t("quiz.lead")}</p>

        <Card className="p-6 space-y-5 bg-card">
          <div>
            <label className="text-sm font-medium mb-2 block">{t("quiz.region")}</label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="quiz-region-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alla">{t("quiz.allRegions")}</SelectItem>
                {regions.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">{t("quiz.count")}</label>
            <Select value={count} onValueChange={setCount}>
              <SelectTrigger data-testid="quiz-count-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["5", "10", "15", "20"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={start} disabled={loading} className="w-full" data-testid="quiz-start-btn">
            <GraduationCap className="size-4 mr-2" />
            {loading ? t("common.preparing") : t("quiz.start")}
          </Button>
        </Card>
      </div>
    );
  }

  if (done) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div data-testid="quiz-done" className="max-w-xl text-center py-10 rise">
        <div className="font-serif text-7xl font-medium text-primary mb-3">{pct}%</div>
        <p className="text-lg text-muted-foreground mb-1">
          {t("quiz.correctText", { score, total: questions.length })}
        </p>
        <p className="text-sm text-muted-foreground mb-8">
          {pct >= 80 ? t("quiz.excellent") : pct >= 60 ? t("quiz.solid") : t("quiz.repeat")}
        </p>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => { setQuestions([]); }} data-testid="quiz-restart-btn">
            <RotateCcw className="size-4 mr-2" /> {t("quiz.new")}
          </Button>
        </div>
      </div>
    );
  }

  const q = questions[idx];

  return (
    <div data-testid="quiz-active" className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <Badge variant="secondary" className="font-mono">{t("quiz.questionOf")} {idx + 1} {t("quiz.of")} {questions.length}</Badge>
        <span className="text-xs text-muted-foreground font-mono">{q.region}</span>
      </div>

      <h2 className="font-serif text-2xl sm:text-3xl font-medium leading-tight mb-7" data-testid="quiz-question">
        {q.question}
      </h2>

      <div className="space-y-2.5">
        {q.options.map((opt, i) => {
          const isCorrect = i === q.correct_index;
          const isSelected = selected === i;
          let cls = "border-border hover:border-primary/40 hover:bg-muted/40";
          if (revealed) {
            if (isCorrect) cls = "border-primary bg-primary/5";
            else if (isSelected) cls = "border-destructive bg-destructive/5";
            else cls = "border-border opacity-60";
          } else if (isSelected) {
            cls = "border-primary bg-primary/5";
          }
          return (
            <button
              key={i}
              onClick={() => !revealed && setSelected(i)}
              data-testid={`quiz-option-${i}`}
              disabled={revealed}
              className={`w-full text-left p-4 rounded-md border transition-all flex items-center justify-between gap-3 ${cls}`}
            >
              <span className="text-sm">{opt}</span>
              {revealed && isCorrect && <CheckCircle2 className="size-5 text-primary shrink-0" />}
              {revealed && isSelected && !isCorrect && <XCircle className="size-5 text-destructive shrink-0" />}
            </button>
          );
        })}
      </div>

      {revealed && (
        <Card className="mt-5 p-4 bg-secondary/40 border-dashed rise" data-testid="quiz-explanation">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{t("quiz.explanation")}</div>
          <p className="text-sm leading-relaxed">{q.explanation}</p>
        </Card>
      )}

      <div className="mt-8 flex gap-3">
        {!revealed ? (
          <Button onClick={reveal} className="flex-1" data-testid="quiz-reveal-btn">{t("quiz.reveal")}</Button>
        ) : (
          <Button onClick={next} className="flex-1" data-testid="quiz-next-btn">
            {idx + 1 >= questions.length ? t("quiz.finish") : t("quiz.next")}
            <ChevronRight className="size-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
